import { useMutation } from "react-query";
import { useAppContext } from "../AppProviders";
import { API_URL } from "./helpers";
import { Item, ProfileConfirmItemRequest } from "./Items";
import {
  CreateProfileRequest,
  Profile,
  ProfileValidationResponse,
  UpdateProfileManualScrapeInput,
} from "./Profiles";

export function useValidateProfile() {
  const { requestApi } = useAppContext();

  const validateProfile = async (
    url: string
  ): Promise<ProfileValidationResponse> => {
    const response = await requestApi.get(
      `${API_URL}/profile/validate?url=${encodeURIComponent(url)}`
    );
    return response.data;
  };

  const mutationResult = useMutation((url: string) => validateProfile(url), {});
  return { ...mutationResult };
}

export function useCreateProfile() {
  const { requestApi } = useAppContext();

  const createProfile = async (
    input: CreateProfileRequest
  ): Promise<Profile> => {
    const response = await requestApi.post(
      `${API_URL}/reports/profiles/${input.report_uuid}?actor_uuid=${input.actor_uuid}`,
      { url: input.profile_url }
    );
    return response.data;
  };

  const mutationResult = useMutation(
    (input: CreateProfileRequest) => createProfile(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateProfileInProgress() {
  const { requestApi } = useAppContext();

  const createProfile = async (
    input: CreateProfileRequest
  ): Promise<Profile> => {
    const response = await requestApi.post(
      `${API_URL}/profile/add/${input.report_uuid}`,
      { url: input.profile_url }
    );
    return response.data;
  };

  const mutationResult = useMutation(
    (input: CreateProfileRequest) => createProfile(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRemoveProfileAndPosts() {
  const { requestApi } = useAppContext();

  const removeProfileAndPosts = async (itemUUID: string): Promise<any> => {
    const response = await requestApi.delete(`${API_URL}/profile/${itemUUID}`);
    return response.data;
  };

  const mutationResult = useMutation(
    (itemId: string) => removeProfileAndPosts(itemId),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRemoveProfile() {
  const { requestApi } = useAppContext();

  const removeProfile = async (itemUUID: string): Promise<any> => {
    const response = await requestApi.delete(
      `${API_URL}/remove_profile_item/${itemUUID}`
    );
    return response.data;
  };

  const mutationResult = useMutation(
    (itemId: string) => removeProfile(itemId),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRemoveClientAddedProfile() {
  const { requestApi } = useAppContext();

  const removeClientAddedProfile = async (itemUUID: string): Promise<any> => {
    const response = await requestApi.delete(
      `${API_URL}/remove_protected_profile_item/${itemUUID}`
    );
    return response.data;
  };

  const mutationResult = useMutation(
    (itemId: string) => removeClientAddedProfile(itemId),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRemoveMultipleProfile() {
  const { requestApi } = useAppContext();

  const removeProfile = async (itemUUIDs: string[]): Promise<any> => {
    const result = [];
    for (let pivot of itemUUIDs) {
      const data = requestApi
        .delete(`${API_URL}/profile/${pivot}`)
        .then((response) => response.data);
      result.push(data);
    }
    return Promise.all(result).then((response) =>
      response.map((item) => {
        if (item[0]) {
          return { ...item[0] };
        }
        return item;
      })
    );
  };

  const mutationResult = useMutation(
    (itemUUIDs: string[]) => removeProfile(itemUUIDs),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateMultipleProfile() {
  const { requestApi } = useAppContext();

  const createProfiles = async (
    input: CreateProfileRequest[]
  ): Promise<Profile[]> => {
    const result = [];
    for (let pivot of input) {
      const data = requestApi
        .post(
          `${API_URL}/reports/profiles/${pivot.report_uuid}?actor_uuid=${pivot.actor_uuid}`,
          { url: pivot.profile_url }
        )
        .then((response) => response.data);
      result.push(data);
    }

    return Promise.all(result).then((response) =>
      response.map((item) => {
        if (item[0]) {
          return { ...item[0] };
        }
        return item;
      })
    );
  };

  const mutationResult = useMutation(
    (input: CreateProfileRequest[]) => createProfiles(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateItemManualScrape() {
  const { requestApi } = useAppContext();

  const updatePostsItemRating = async (
    input: UpdateProfileManualScrapeInput
  ): Promise<Item> => {
    const updatedProfile = {
      content: { ...input.profile.content, scrape_status: input.status },
    };

    return requestApi
      .put(
        `${API_URL}/itemdal/items/${input.profile.external_id}`,
        updatedProfile
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: UpdateProfileManualScrapeInput) => updatePostsItemRating(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useDeleteProfilePosts() {
  const { requestApi } = useAppContext();

  const deleteProfilePosts = async (profileUUID: string): Promise<Item> => {
    return requestApi
      .delete(`${API_URL}/posts/${profileUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (profileUUID: string) => deleteProfilePosts(profileUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateProfileQA() {
  const { requestApi } = useAppContext();

  const createProfile = async (
    input: CreateProfileRequest
  ): Promise<Profile> => {
    const response = await requestApi.post(
      `${API_URL}/profile/qa/add/${input.report_uuid}`,
      { url: input.profile_url }
    );
    return response.data;
  };

  const mutationResult = useMutation(
    (input: CreateProfileRequest) => createProfile(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useManualUpdateProfileContent() {
  const { requestApi } = useAppContext();

  const manualUpdateProfileContent = async (
    input: ProfileConfirmItemRequest
  ): Promise<Item> => {
    const response = await requestApi.put(
      `${API_URL}/manual_update_profile_content`,
      input
    );
    return response.data;
  };

  const mutationResult = useMutation(
    (input: ProfileConfirmItemRequest) => manualUpdateProfileContent(input),
    {}
  );

  return {
    ...mutationResult,
  };
}
